import { Divider, Grid, Box as MuiBox, Typography } from '@mui/material';
import React, { memo } from 'react';
import './PaymentReviewDetails.scss';
import { PaymentReviewDetailsProps } from './PaymentReviewDetails.types';

const SPACING_BETWEEN_ELEMENTS = 2;

const getDateOfBirth = (originalDate: string) =>
  (originalDate && originalDate.split('T')[0].split('-').reverse().join('/')) ||
  undefined;

export const getAdditionalInsuredPeople = (additionalInsured: any[]): any[] => {
  const additionalInsuredPeople: any = [];
  additionalInsured?.forEach(({ firstName, lastName, dob_utc }) => {
    additionalInsuredPeople.push({
      name: `${firstName} ${lastName}`,
      dob: getDateOfBirth(dob_utc),
    });
  });

  return additionalInsuredPeople;
};

export const getInterestedPeople = (additionalInterested: any[]): any[] => {
  const additionalInterestedPeople: any[] = [];
  additionalInterested?.forEach(({ commercial_name, address }) => {
    const {
      address_number,
      street,
      sub_address_number,
      city,
      state,
      postal_code,
    } = address;
    additionalInterestedPeople.push({
      name: commercial_name,
      address: `${address_number} ${street} Unit ${sub_address_number}, ${city}. ${state}. ${postal_code}`,
    });
  });

  return additionalInterestedPeople;
};

export const getPeopleInDetails = (
  renterInfo: any,
  insuredPeople: any[],
  interestedPeople: any[],
) => {
  const { name, email, dob_utc, marital_status, phone } = renterInfo;
  const { given_name, family_name } = name || {};
  const insured = getAdditionalInsuredPeople(insuredPeople);
  const interested = getInterestedPeople(interestedPeople);

  return [
    {
      name: `${given_name} ${family_name}`,
      email,
      dob: getDateOfBirth(dob_utc),
      maritalStatus: marital_status,
      phone: phone,
    },
    ...insured,
    ...interested,
  ];
};

const isEmpty = (value: string): boolean => !value || /^\s*$/.test(value);

const formatPhone = (value: string) =>
  value &&
  `(${value.split(' ')[0]}) ${value.replace(/[0-9]* /, '').replace(' ', '-')}`;

export const PaymentReviewDetails: React.FC<PaymentReviewDetailsProps> = memo(
  ({
    id,
    address,
    peopleInPolicy,
    coverages,
    coverageStartDate,
    buildingUnit,
    additionalInsuredRoommates,
    additionalInterests,
  }) => {
    const hasPeopleInPolicy = () =>
      peopleInPolicy?.some((people) => !isEmpty(people.name));
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    });

    return (
      <>
        <Grid
          container
          id={id}
          sx={{
            borderRadius: 2,
            boxShadow: '4px 8px 22px rgba(29, 29, 29, 0.08)',
            width: '100%',
            flexWrap: 'nowrap',
            overflowWrap: 'break-word',
          }}
          direction="column"
          padding={3}
          rowSpacing={SPACING_BETWEEN_ELEMENTS}
        >
          <Grid item>
            <Typography variant="h4">Your Policy</Typography>
          </Grid>

          {address && (address.state || address.region) && (
            <>
              <Grid item>
                {address.addressNumber && address.addressNumber!='' && address.addressNumber} {address.street},{' '}
                {address.municipality}, {address.state || address.region}{' '}
                {address.postal_code} {address.postalCode}
              </Grid>
              <Grid item>
                <Typography variant="body1">Unit {buildingUnit}</Typography>
              </Grid>
            </>
          )}
          {coverageStartDate && (
            <Grid
              container
              justifyContent="space-between"
              alignItems="baseline"
              marginTop={SPACING_BETWEEN_ELEMENTS}
            >
              <Grid item>
                <Typography variant="body1" fontWeight={'500'}>Start Date</Typography>
              </Grid>
              <Grid item>
                <Typography variant="body1">{coverageStartDate}</Typography>
              </Grid>
            </Grid>
          )}
          {coverages && (
            <>
              <Grid item>
                <Divider style={{ width: '100%' }} flexItem />
              </Grid>

              {coverages.map(({ name, cost }) => (
                <Grid item>
                  <Grid container justifyContent="space-between" marginY={-0.5}>
                    <Typography variant="body1" fontWeight={'500'}>{name}</Typography>
                    <Typography variant="body1">
                      {formatter.format(parseInt(String(cost)))}
                    </Typography>
                  </Grid>
                </Grid>
              ))}
            </>
          )}
          <Grid item>
            <Divider style={{ width: '100%' }} flexItem />
          </Grid>
          {peopleInPolicy && hasPeopleInPolicy() && (
            <Grid item>
              <Typography variant="body1" fontWeight={'500'}>
                Your Details
                <br />
              </Typography>
              {peopleInPolicy.map(
                (
                  { name, email, dob, maritalStatus, coverage, address, phone },
                  index,
                ) => (
                  <MuiBox key={'policy_people' + index}>
                    <Typography variant="body1">
                      {name}
                      <br />
                    </Typography>
                    <Typography variant="body1">
                      {email && (
                        <>
                          {email}
                          <br />
                        </>
                      )}
                      {dob && (
                        <>
                          {dob}
                          <br />
                        </>
                      )}
                      {phone && (
                        <>
                          {formatPhone(phone)}
                          <br />
                        </>
                      )}
                      {maritalStatus && (
                        <>
                          {maritalStatus}
                          <br />
                        </>
                      )}
                      {coverage && (
                        <>
                          {coverage}
                          <br />
                        </>
                      )}
                      {address && (
                        <>
                          {address}
                          <br />
                        </>
                      )}
                    </Typography>
                  </MuiBox>
                ),
              )}
            </Grid>
          )}
          {Array.isArray(additionalInterests) &&
            !!additionalInterests.length && (
              <Grid item>
                <Typography variant="body1" fontWeight={'500'}>
                  Landlord or Additional Interests
                </Typography>
                {additionalInterests.map(
                  (
                    {
                      name,
                      email,
                      dob,
                      maritalStatus,
                      coverage,
                      address,
                      phone,
                    },
                    index,
                  ) => (
                    <div key={'additionalInterests' + index}>
                      <Typography variant="body1">
                        {name}
                        <br />
                      </Typography>
                      <Typography variant="body1">
                        {email && (
                          <>
                            {email}
                            <br />
                          </>
                        )}
                        {dob && (
                          <>
                            {dob}
                            <br />
                          </>
                        )}
                        {phone && (
                          <a href={'tel:' + phone}>
                            {formatPhone(phone)}
                            <br />
                          </a>
                        )}
                        {maritalStatus && (
                          <>
                            {maritalStatus}
                            <br />
                          </>
                        )}
                        {coverage && (
                          <>
                            {coverage}
                            <br />
                          </>
                        )}
                        {address && (
                          <>
                            {address}
                            <br />
                          </>
                        )}
                      </Typography>
                    </div>
                  ),
                )}
              </Grid>
            )}
          {Array.isArray(additionalInsuredRoommates) &&
            !!additionalInsuredRoommates.length && (
              <Grid item>
                <Typography variant="body1" fontWeight={'500'}>Others Insured</Typography>
                {additionalInsuredRoommates.map(
                  (
                    {
                      name,
                      email,
                      dob,
                      maritalStatus,
                      coverage,
                      address,
                      phone,
                    },
                    index,
                  ) => (
                    <div key={'additionalInsuredRoommates' + index}>
                      <Typography variant="body1">{name}</Typography>
                      <Typography variant="body1">
                        {email && <>{email}</>}
                        {dob && (
                          <>
                            {dob}
                            <br />
                          </>
                        )}
                        {phone && (
                          <a href={'tel:' + phone}>
                            {formatPhone(phone)}
                            <br />
                          </a>
                        )}
                        {maritalStatus && (
                          <>
                            {maritalStatus}
                            <br />
                          </>
                        )}
                        {coverage && (
                          <>
                            {coverage}
                            <br />
                          </>
                        )}
                        {address && (
                          <>
                            {address}
                            <br />
                          </>
                        )}
                      </Typography>
                    </div>
                  ),
                )}
              </Grid>
            )}
        </Grid>
      </>
    );
  },
);
